import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import clsx from 'clsx'

import InteractiveElement from '$extensionSrc/components/InteractiveElement'
import { extensionDashboardUrl } from '$extensionSrc/consts/urlConsts'
import { PlanPaymentView } from '$extensionSrc/metrics/entities/plan/types'
import { track } from '$extensionSrc/metrics/track'
import { getExtensionUrl } from '$serverSrc/utils/urlUtils'

import styles from './Payment.module.scss'
import { authFetch } from '../utils/fetch'

export default function Payment({
  browser,
  code,
  extensionId,
  isDisabledBecauseInternational,
  percentOff,
  stripeCouponId,
  stripePublishableKey,
}: {
  browser: 'chrome' | 'firefox' | 'edge'
  code: string | null
  extensionId: string
  isDisabledBecauseInternational: boolean
  percentOff: number | null
  stripeCouponId: string | null
  stripePublishableKey?: string
}) {
  const stripe: Promise<Stripe | null> | null = useMemo(
    () => (stripePublishableKey ? loadStripe(stripePublishableKey) : null),
    [stripePublishableKey],
  )

  const [isPromoCheckoutSession, setIsPromoCheckoutSession] = useState<boolean>(
    code !== null && code !== undefined,
  )

  const handlePayingForAPlanClick = useCallback(() => {
    setIsPromoCheckoutSession(false)
  }, [])

  const fetchClientSecret = useCallback(async () => {
    const request = new Request('/api/payment/checkout-session/', {
      body: JSON.stringify({
        code,
        isPromoCheckoutSession,
        allowPromotionCodes: true,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
    const res = await authFetch(request)
    const data = await res.json()
    return data.clientSecret
  }, [code, isPromoCheckoutSession])

  useEffect(() => {
    const interaction: PlanPaymentView = {
      name: 'plan:payment:view',
      properties: {
        code: isPromoCheckoutSession ? code : null,
        stripe_coupon_id: isPromoCheckoutSession ? stripeCouponId : null,
      },
    }
    track(interaction)
  }, [code, stripeCouponId, isPromoCheckoutSession, stripePublishableKey])

  const banner: ReactNode = useMemo(() => {
    // By code
    if (code === 'YAHOOPILOT') {
      return 'Activate your Yahoo-sponsored access'
    }

    // General catch all
    if (code) {
      if (percentOff === 100) {
        return (
          <>
            <p>Get your first year free using your promo code</p>
            <p className="body2">
              Or if you&apos;d like to support our work by{' '}
              <InteractiveElement
                className={clsx('darkest-gray', 'link2')}
                onClick={handlePayingForAPlanClick}
              >
                paying for a plan
              </InteractiveElement>
              , we really appreciate it
            </p>
          </>
        )
      }
      return `Get your first year for ${percentOff}% off with promo code ${code}`
    }
    return null
  }, [code, handlePayingForAPlanClick, percentOff])

  if (isDisabledBecauseInternational) {
    const dashboardUrl = getExtensionUrl(
      extensionDashboardUrl,
      browser,
      extensionId,
    )
    return (
      <div className={clsx(styles.refundPolicy)}>
        <div className="center">
          <div className="body2">
            Sorry! We do not currently support payment in your country.{' '}
            <a href={dashboardUrl} target="_blank" rel="noreferrer">
              Click here
            </a>{' '}
            to return to the extension.
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {isPromoCheckoutSession && banner && (
        <div className={clsx(styles.banner, 'darkest-gray', 'center', 'body1')}>
          <div>
            <div>{banner}</div>
          </div>
        </div>
      )}
      {(!isPromoCheckoutSession || percentOff !== 100) && (
        <div
          className={clsx(
            styles.refundPolicy,
            percentOff !== 100 && banner && styles.withBanner,
          )}
        >
          <div className="center">
            <div className="body2">
              By purchasing a plan, you are also agreeing to our{' '}
              <a
                className={clsx('darkest-gray', 'link2')}
                href="https://www.blockpartyapp.com/tos/"
                rel="noreferrer"
                target="_blank"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      )}
      {stripePublishableKey && (
        <EmbeddedCheckoutProvider
          key={isPromoCheckoutSession ? 'promo' : 'not promo'}
          options={{ fetchClientSecret }}
          stripe={stripe}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      )}
      {!stripePublishableKey && (
        <div className={styles.stripePlaceholder}>
          {isPromoCheckoutSession && percentOff === 100 && (
            <div>Promo code Stripe widget</div>
          )}
          {(!isPromoCheckoutSession || percentOff !== 100) && (
            <div>Non-promo code Stripe widget</div>
          )}
        </div>
      )}
    </>
  )
}
